import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Formik } from 'formik';
import clsx from 'clsx';
import { TextField } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { actions } from '../../../store/ducks/auth/actions';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { passwordHelper, forgotPassText } from '../../../../constants';
import { EMAIL_REGEX, PASSWORD_REGEX, VERIFICATION_CODE_REGEX } from '../../../constants/regex';
import styles from './ForgotPasswordConfirm.module.scss';
import DialogNotifyWindow from '../../../components/DialogNotifyWindow/DialogNotifyWindow';
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons';
import { AuthWrapper } from 'app/Wrappers/AuthWrapper';
import errorIcon from 'app/assets/images/icons/errorIcon.jpg';
import successIcon from 'app/assets/images/icons/successIcon.jpg';
import ReCAPTCHA from 'react-google-recaptcha';
import config from 'config';
import { 
  executeRecaptcha, 
  createOnRecaptchaExpired, 
  createOnRecaptchaErrored, 
  handleRecaptchaError 
} from '../../../utils/recaptchaUtils';

const ForgotPasswordConfirm = () => {
  const [open, setOpen] = useState(false);
  const recaptchaRef = useRef();
  const mountedRef = useRef(true);
  const executingCaptchaRef = useRef(false);
  const { forgotPassError } = actions;
  const { errorSet, loading } = useSelector(
      state => ({
        errorSet: state.auth.forgotPassError,
        loading: state.auth.loading
      })
    );
  const history = useHistory();
  const dispatch = useDispatch();
  const [isExecutingCaptcha, setIsExecutingCaptcha] = useState(false);

  // Cleanup mountedRef on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // URL parameter validation
  const validateUrlParams = () => {
    try {
      const { location } = window;
      const urlParams = new URLSearchParams(location.search);
      const verificationCode = urlParams.get('code');
      const email = urlParams.get('email');

      // Check for presence
      if (!verificationCode || !email) {
        return { 
          isValid: false, 
          error: 'Missing required parameters',
          verificationCode: null,
          email: null 
        };
      }

      // Validate email format
      if (!EMAIL_REGEX.test(email)) {
        return { 
          isValid: false, 
          error: 'Invalid email format',
          verificationCode: null,
          email: null 
        };
      }

      // Validate verification code format (6 digits only)
      if (!VERIFICATION_CODE_REGEX.test(verificationCode)) {
        return { 
          isValid: false, 
          error: 'Invalid verification code format',
          verificationCode: null,
          email: null 
        };
      }

      return { 
        isValid: true, 
        error: null,
        verificationCode,
        email 
      };
    } catch (error) {
      return { 
        isValid: false, 
        error: 'Malformed URL parameters',
        verificationCode: null,
        email: null 
      };
    }
  };

  const { isValid, error, verificationCode, email } = validateUrlParams();

  const handleResend = () => {
    history.push('/auth/forgot-password');
  };

  // Early return for invalid parameters
  if (!isValid) {
    return (
      <AuthWrapper>
        <div className="kt-login__body">
          <div className="kt-login__form">
            <div className="kt-login__title">
              <p className="login-subtitle">
                The reset link has expired or is not valid.
              </p>
            </div>
            <div>
              <button
                onClick={handleResend}
                className={`btn ${styles.resendBtn}`}
              >
                Resend
              </button>
            </div>
          </div>
        </div>
      </AuthWrapper>
    );
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const clearErrorPanel = () => {
    dispatch(forgotPassError(''));
  };

  const handleClose = () => {
    setTimeout(() => setOpen(false), 500);
    history.push('/auth');
  };

  // Create reCAPTCHA event handlers using shared utility
  const onRecaptchaExpired = createOnRecaptchaExpired({
    recaptchaRef,
    dispatch,
    errorAction: forgotPassError
  });

  const onRecaptchaErrored = createOnRecaptchaErrored({
    recaptchaRef,
    dispatch,
    errorAction: forgotPassError
  });

  const passwordVerification = async (values, { setStatus, setSubmitting }) => {
    const { email, verificationCode, password } = values;
    
    try {
      let recaptchaToken = null;
      
      // Add this reCAPTCHA block
      if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) {
        try {
          recaptchaToken = await executeRecaptcha({
            recaptchaRef,
            mountedRef,
            executingCaptchaRef,
            setIsExecutingCaptcha,
            dispatch,
            errorAction: forgotPassError
          });
        } catch (e) {
          handleRecaptchaError(e, dispatch, forgotPassError);
          setSubmitting(false);
          return;
        }
        if (!recaptchaToken) {
          dispatch(forgotPassError('reCAPTCHA verification failed. Please try again.'));
          setSubmitting(false);
          return;
        }
      }

      const callback = (success) => {
        try {
          if (success) {
            localStorage.removeItem('verifyEmail');
            localStorage.removeItem('tempAuthData');
            setSubmitting(false);
            handleClickOpen();
          } else {
            setSubmitting(false);
          }
        } catch (err) {
          console.error('Password reset error:', err);
          setSubmitting(false);
          setStatus('An unexpected error occurred. Please try again.');
        }
      };

      dispatch(actions.forgotPasswordConfirm({
        email,
        confirmationCode: verificationCode,
        newPassword: password,
        recaptchaToken
      }, callback));
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      dispatch(forgotPassError('An error occurred during password reset confirmation. Please try again.'));
      setSubmitting(false);
    }
  };

  return (
    <>
      <AuthWrapper>
        <div className="login-form-title">Confirm Password</div>
        <div className="login-form-summary">
          Enter a new password for the <br />
          {email} account
        </div>
        <div className="kt-login__body" style={{ position: 'relative' }}>
          <div className="kt-login__form">
            <Formik
              initialValues={{
                email,
                verificationCode,
                password: '',
                confirmPassword: '',
              }}
              validate={values => {
                const errors = {};

                // Validate required URL parameters (double-check)
                if (!values.email) {
                  errors.email = 'Email is required';
                } else if (!EMAIL_REGEX.test(values.email)) {
                  errors.email = 'Invalid email format';
                }

                if (!values.verificationCode) {
                  errors.verificationCode = 'Verification code is required';
                } else if (!VERIFICATION_CODE_REGEX.test(values.verificationCode)) {
                  errors.verificationCode = 'Verification code must be 6 digits';
                }

                // Validate password match
                if (
                  values.password &&
                  values.confirmPassword &&
                  values.password !== values.confirmPassword
                ) {
                  errors.confirmPassword =
                    "Password and Confirm Password didn't match.";
                }

                // Validate password criteria
                if (values.password && !PASSWORD_REGEX.test(values.password)) {
                  errors.password = 'Password does not meet criteria';
                }

                return errors;
              }}
              onSubmit={(values, formikBag) => {
                // Clear any previous errors
                dispatch(forgotPassError(''));
                formikBag.setStatus(null);

                // Additional validation before submission
                if (!values.email || !EMAIL_REGEX.test(values.email)) {
                  formikBag.setStatus('Invalid email address. Please request a new reset link.');
                  formikBag.setSubmitting(false);
                  return;
                }

                if (!values.verificationCode || !VERIFICATION_CODE_REGEX.test(values.verificationCode)) {
                  formikBag.setStatus('Invalid verification code. Verification codes must be 6 digits.');
                  formikBag.setSubmitting(false);
                  return;
                }

                formikBag.setSubmitting(true);
                passwordVerification(values, formikBag);
              }}
            >
              {({
                values,
                status,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className="kt-form">
                  {config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      size="invisible"
                      sitekey={config.recaptcha.SITE_KEY}
                      onErrored={onRecaptchaErrored}
                      onExpired={onRecaptchaExpired}
                    />
                  )}
                  {errorSet ? (
                    <div role="alert" className="alert alert-danger">
                      <div className="alert-text">{errorSet}</div>
                    </div>
                  ) : (
                    status && (
                      <div role="alert" className="alert alert-danger">
                        <div className="alert-text">{status}</div>
                      </div>
                    )
                  )}

                  <div className="form-group">
                    <div className={styles.passwordBlock}>
                      <TextField
                        type="password"
                        margin="normal"
                        fullWidth={true}
                        name="password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.password}
                        helperText={touched.password && errors.password}
                        error={Boolean(touched.password && errors.password)}
                        className="kt-width-full custom-form-label"
                        placeholder="New password"
                      />
                      <div className={styles.tooltipInfo}>
                        <Tooltip
                          title={passwordHelper}
                          position="bottom-start"
                        />
                      </div>
                      {Boolean(touched.password && errors.password) ? (
                        <img
                          className={styles.passwordIcon}
                          src={errorIcon}
                          alt="icon"
                        />
                      ) : (
                        Boolean(
                          touched.password &&
                            values.password &&
                            !errors.password,
                        ) && (
                          <img
                            className={styles.passwordIcon}
                            src={successIcon}
                            alt="icon"
                          />
                        )
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <TextField
                      type="password"
                      margin="normal"
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.confirmPassword}
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword,
                      )}
                      className="kt-width-full custom-form-label"
                      placeholder="Confirm new password"
                    />
                    {Boolean(
                      touched.confirmPassword && errors.confirmPassword,
                    ) ? (
                      <img
                        className={styles.confirmIcon}
                        src={errorIcon}
                        alt="icon"
                      />
                    ) : (
                      Boolean(
                        touched.confirmPassword &&
                          values.confirmPassword &&
                          !errors.confirmPassword,
                      ) && (
                        <img
                          className={styles.confirmIcon}
                          src={successIcon}
                          alt="icon"
                        />
                      )
                    )}
                  </div>
                  <div className="login-form-actions">
                    <button
                      type="submit"
                      className={`btn button-login ${clsx({
                        'kt-spinner kt-spinner--right kt-spinner--md kt-spinner--light':
                          loading || isSubmitting || isExecutingCaptcha,
                      })}`}
                      disabled={
                        !values.password ||
                        !values.confirmPassword ||
                        !values.email ||
                        !values.verificationCode ||
                        errors.password ||
                        errors.confirmPassword ||
                        errors.email ||
                        errors.verificationCode ||
                        loading ||
                        isSubmitting ||
                        isExecutingCaptcha
                      }
                      onClick={clearErrorPanel}
                    >
                      Confirm Password
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
          <div className="kt-login__back">
            <Link to="/auth">
              <ArrowBackIcon />
            </Link>
            <span>Back to Sign in</span>
          </div>
        </div>
        {errorSet || loading ? (
          ''
        ) : (
          <DialogNotifyWindow
            text={forgotPassText}
            errorSet={errorSet}
            open={open}
            handleClose={handleClose}
          />
        )}
      </AuthWrapper>
    </>
  );
};

export default ForgotPasswordConfirm;
